// src/pages/admin/RoleMaster.tsx - FIXED WITH PROPER SUPER ADMIN API ACCESS
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Drawer,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useAddRoleMutation,
  useListRolesQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  Role,
} from '../../features/admin/roleMasterApi.ts';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const RoleMaster = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    role_name: '',
    role_desc: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [apiError, setApiError] = useState<string>('');

  // Get current admin info and check Super Admin status
  useEffect(() => {
    const checkSuperAdmin = () => {
      const adminType = localStorage.getItem('admin_type') || 
                       localStorage.getItem('user_role') || 
                       'operation';
      
      const cleanRole = adminType.toString().toLowerCase().trim();
      const isSuper = cleanRole.includes('super') || 
                      cleanRole === 'super_admin' || 
                      cleanRole === 'super admin' ||
                      cleanRole === 'superadmin' ||
                      cleanRole === 'super';
      
      console.log('🔍 RoleMaster - Current Admin Role:', adminType);
      console.log('🔍 RoleMaster - Clean Role:', cleanRole);
      console.log('🔍 RoleMaster - Is Super Admin:', isSuper);
      
      setIsSuperAdmin(isSuper);
      return isSuper;
    };

    checkSuperAdmin();
    
    // Also check if admin token exists
    const adminToken = localStorage.getItem('admin_token');
    console.log('🔍 Admin Token exists:', !!adminToken);
    console.log('🔍 Admin Token value:', adminToken ? 'Present' : 'Missing');
    
    if (!adminToken) {
      console.warn('⚠️ No admin token found in localStorage');
      setApiError('Authentication token not found. Please login again.');
    }
  }, []);

  // RTK Query hooks with proper authentication
  const { 
    data: rolesData, 
    isLoading: isLoadingRoles, 
    refetch: refetchRoles,
    error: rolesError,
    isError: isRolesError 
  } = useListRolesQuery(undefined, {
    skip: !isSuperAdmin, // Only call API if super admin
  });
  
  const [addRole, { isLoading: isAdding }] = useAddRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

  // Update API error state
  useEffect(() => {
    if (rolesData?.error) {
      setApiError(rolesData.message || 'API Error');
    } else if (isRolesError) {
      setApiError('Failed to connect to server. Please check your connection.');
    } else {
      setApiError('');
    }
  }, [rolesData, isRolesError]);

  // Check if user has permission (only super admin)
  const hasPermission = () => {
    return isSuperAdmin;
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditMode(true);
      setSelectedRole(role);
      setFormData({
        role_name: role.role_name,
        role_desc: role.role_desc || '',
      });
    } else {
      setEditMode(false);
      setSelectedRole(null);
      setFormData({
        role_name: '',
        role_desc: '',
      });
    }
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedRole(null);
    setFormData({
      role_name: '',
      role_desc: '',
    });
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.role_name.trim()) {
      newErrors.role_name = 'Role name is required';
    }

    if (!formData.role_desc.trim()) {
      newErrors.role_desc = 'Role description is required';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      if (editMode && selectedRole) {
        const result = await updateRole({
          id: selectedRole.id,
          role_name: formData.role_name.trim(),
          role_desc: formData.role_desc.trim(),
        }).unwrap();

        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role updated successfully',
            severity: 'success',
          });
          
          // Refresh data
          refetchRoles();
          
          setTimeout(() => {
            handleCloseDialog();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to update role',
            severity: 'error',
          });
        }
      } else {
        const result = await addRole({
          role_name: formData.role_name.trim(),
          role_desc: formData.role_desc.trim(),
        }).unwrap();

        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role added successfully',
            severity: 'success',
          });
          
          // Refresh data
          refetchRoles();
          
          setTimeout(() => {
            handleCloseDialog();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to add role',
            severity: 'error',
          });
        }
      }
    } catch (error: any) {
      console.error('Role operation error:', error);
      
      let errorMessage = 'Operation failed';
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.error) {
        errorMessage = error.error;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        const result = await deleteRole(id).unwrap();
        
        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role deleted successfully',
            severity: 'success',
          });
          
          // Refresh data
          refetchRoles();
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to delete role',
            severity: 'error',
          });
        }
      } catch (error: any) {
        console.error('Delete role error:', error);
        setSnackbar({
          open: true,
          message: error?.data?.message || 'Failed to delete role',
          severity: 'error',
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString.replace(' ', 'T'));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  };

  // If user doesn't have permission (only super admin can access)
  if (!isSuperAdmin) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
        <Box sx={{ display: 'flex', flex: 1 }}>
          {/* Desktop Sidebar */}
          <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
            <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
          </Box>
          
          {/* Main Content */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <AdminHeader onToggleMenu={handleDrawerToggle} />
            
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 3 
            }}>
              <Alert severity="error" sx={{ maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>
                  ⚠️ Access Denied
                </Typography>
                <Typography>
                  You do not have permission to access Role Master.
                  <br />
                  <strong>Only Super Admin</strong> can manage roles.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: '#fef2f2', borderRadius: 1 }}>
                  <strong>Your role:</strong> {localStorage.getItem('admin_type') || localStorage.getItem('user_role') || 'Unknown'}
                  <br />
                  <strong>Required role:</strong> Super Admin
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }}
                  onClick={() => window.history.back()}
                >
                  Go Back
                </Button>
              </Alert>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const displayRoles = rolesData?.roles || [];
  const loading = isLoadingRoles;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SecurityIcon sx={{ fontSize: 28, color: '#008060' }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Role Master
                  </Typography>
                  <Chip 
                    label="Super Admin Only" 
                    size="small" 
                    color="warning" 
                    sx={{ fontWeight: 600 }}
                  />
                  {apiError && (
                    <Chip 
                      label="API Error" 
                      size="small" 
                      color="error" 
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#008060',
                    fontWeight: 600,
                    borderRadius: '8px',
                    px: 2.5,
                    '&:hover': { bgcolor: '#006e52' },
                    '&:disabled': { bgcolor: '#e1e3e5', color: '#6d7175' }
                  }}
                >
                  Add Role
                </Button>
              </Box>

              {/* Error/Alerts */}
              {apiError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    API Connection Issue
                  </Typography>
                  <Typography variant="body2">
                    {apiError}
                    <br />
                    <strong>Note:</strong> Make sure you're logged in as Super Admin.
                  </Typography>
                </Alert>
              )}

              {isRolesError && !apiError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Failed to load roles
                  </Typography>
                  <Typography variant="body2">
                    {(rolesError as any)?.data?.message || 'Please check your connection and try again.'}
                  </Typography>
                </Alert>
              )}

              {/* Roles Table */}
              <Card
                sx={{
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  border: '1px solid #e1e3e5',
                  overflow: 'hidden',
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 300,
                      bgcolor: 'white',
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                    <Typography sx={{ ml: 2 }}>Loading roles...</Typography>
                  </Box>
                ) : displayRoles.length > 0 ? (
                  <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                    {/* Table Header */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '80px 1fr 2fr 150px 150px 120px',
                        gap: 2,
                        px: 3,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                        minWidth: '800px',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        S.No
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Role Name
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Description
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Created Date
                      </Typography>
                     
                     
                    </Box>

                    {/* Table Rows */}
                    {displayRoles.map((role, index) => (
                      <Box
                        key={role.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '80px 1fr 2fr 150px 150px 120px',
                          gap: 2,
                          px: 3,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: '1px solid #e1e3e5',
                          '&:hover': { bgcolor: '#f9fafb' },
                          '&:last-child': { borderBottom: 'none' },
                          transition: 'background-color 0.15s ease',
                          minWidth: '800px',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#6d7175', fontWeight: 500 }}>
                          {index + 1}
                        </Typography>

                        <Box>
                          <Chip
                            label={role.role_name}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1565c0',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                          {role.role_desc || 'N/A'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(role.createddt)}
                        </Typography>

                       
                        
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <SecurityIcon sx={{ fontSize: 64, color: '#c4cdd5', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No roles found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {apiError 
                        ? 'Unable to load data from server. Please check your connection.' 
                        : 'Click "Add Role" to create your first role'}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                      disabled={loading}
                      sx={{
                        textTransform: 'none',
                        bgcolor: '#008060',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' },
                        '&:disabled': { bgcolor: '#e1e3e5', color: '#6d7175' }
                      }}
                    >
                      Add Role
                    </Button>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e1e3e5',
            fontWeight: 600,
            color: '#202223',
          }}
        >
          {editMode ? 'Edit Role' : 'Add New Role'}
          <IconButton size="small" onClick={handleCloseDialog} disabled={isAdding || isUpdating}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Role Name"
                name="role_name"
                value={formData.role_name}
                onChange={handleInputChange}
                error={!!formErrors.role_name}
                helperText={formErrors.role_name || 'Enter role name (e.g., Admin, Manager)'}
                disabled={isAdding || isUpdating}
                placeholder="Enter role name"
                required
              />
              <TextField
                fullWidth
                label="Role Description"
                name="role_desc"
                value={formData.role_desc}
                onChange={handleInputChange}
                error={!!formErrors.role_desc}
                helperText={formErrors.role_desc || 'Enter a brief description of the role'}
                multiline
                rows={3}
                disabled={isAdding || isUpdating}
                placeholder="Enter role description"
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #e1e3e5' }}>
            <Button
              onClick={handleCloseDialog}
              disabled={isAdding || isUpdating}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#202223',
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isAdding || isUpdating}
              sx={{
                textTransform: 'none',
                bgcolor: '#008060',
                fontWeight: 600,
                borderRadius: '8px',
                px: 2.5,
                '&:hover': { bgcolor: '#006e52' },
              }}
            >
              {isAdding || isUpdating ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {editMode ? 'Updating...' : 'Adding...'}
                </Box>
              ) : editMode ? (
                'Update Role'
              ) : (
                'Add Role'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleMaster;